/**
 * Usage analytics + performance monitoring for the web app.
 *
 * Mirrors the conventions in mobile/src/analytics/analytics.ts on purpose: both
 * platforms report into the SAME GA4 property, so event names and the
 * `auth_state` user property have to line up or the reports split in two.
 *
 * Nothing here records prospecting data (waypoint titles, notes, sample values,
 * positions the user is looking at). Only WHICH features get used, plus coarse
 * map zoom. Anything that could identify a person or a target belongs behind a
 * consent prompt and in the privacy policy first.
 *
 * Analytics is production-only: getAnalytics() sets cookies on the current
 * domain, which throws on localhost.
 */
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';
import { app } from './firebase';

const enabled = process.env.NODE_ENV === 'production';

let analytics = null;
let perf = null;

if (enabled) {
  // isSupported() is false in some privacy modes, in-app browsers and when an
  // ad blocker has removed the measurement endpoint. Never assume an instance.
  isSupported()
    .then(ok => {
      if (ok) analytics = getAnalytics(app);
    })
    .catch(() => {
      /* leave analytics null */
    });

  try {
    perf = getPerformance(app);
  } catch {
    /* performance is best-effort */
  }
}

/** Telemetry must never break the app or surface an error to the user. */
function safe(fn) {
  try {
    Promise.resolve(fn()).catch(() => {});
  } catch {
    /* ignore */
  }
}

/**
 * Log an event. In development this logs to the console instead so call sites
 * can be verified without a production build.
 */
export function track(event, params = {}) {
  if (analytics) safe(() => logEvent(analytics, event, params));
  else if (!enabled) console.debug('[track]', event, params);
}

/** uid when signed in, null clears it (sign-out). */
export function setAnalyticsUser(uid) {
  safe(() => analytics && setUserId(analytics, uid));
}

/**
 * 'signed_out' | 'signed_in' | 'premium'
 *
 * Set on boot and whenever auth or entitlement changes. This is the property
 * that makes every other report splittable by paying vs non-paying, which is
 * the whole reason for wiring identity through.
 */
export function setAuthState(state) {
  safe(() => analytics && setUserProperties(analytics, { auth_state: state }));
}

export function setIsAdmin(isAdmin) {
  safe(() => analytics && setUserProperties(analytics, { is_admin: String(!!isAdmin) }));
}

/**
 * SPA page view. GA4 only auto-fires page_view on history changes when
 * Enhanced Measurement's "page changes based on browser history events" is
 * enabled; logging it manually makes route tracking independent of that admin
 * setting (and avoids double counting being the only failure mode).
 */
export function trackPageView(path, title) {
  track('page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  });
}

/**
 * Wrap an async operation in a Performance Monitoring custom trace.
 * Returns whatever fn() returns; a missing perf instance is a passthrough.
 */
export async function timed(name, fn, attrs = {}) {
  if (!perf) return fn();

  let t;
  try {
    t = trace(perf, name);
    Object.entries(attrs).forEach(([k, v]) => t.putAttribute(k, String(v)));
    t.start();
  } catch {
    return fn();
  }

  try {
    return await fn();
  } finally {
    safe(() => t.stop());
  }
}

/**
 * WFS query outcome.
 *
 * `cache_hit` is the point of this one: WFS_PERFORMANCE_OPTIMIZATIONS.md
 * describes an LRU cache, bbox-overlap suppression and debouncing, but nothing
 * has ever measured whether they hit in the field.
 *
 * Buckets duration instead of reporting raw ms as a dimension so GA4 can group
 * it; the raw value stays in `duration_ms` as a metric.
 */
export function trackWfsQuery({ layer, cacheHit, durationMs, featureCount, status = 'ok' }) {
  track('wfs_query', {
    layer,
    cache_hit: cacheHit ? 'hit' : 'miss',
    status,
    duration_ms: Math.round(durationMs || 0),
    duration_bucket: durationBucket(durationMs),
    feature_count: featureCount ?? 0,
  });
}

function durationBucket(ms) {
  if (ms == null) return 'unknown';
  if (ms < 250) return '0-250ms';
  if (ms < 1000) return '250ms-1s';
  if (ms < 3000) return '1-3s';
  if (ms < 10000) return '3-10s';
  return '10s+';
}

/**
 * Named events, so spelling stays consistent across call sites and matches
 * mobile where the two platforms do the same thing.
 */
export const Events = {
  // --- auth / entitlement funnel ---
  signInStart: method => track('sign_in_start', { method }),
  signInDone: method => track('sign_in_complete', { method }),
  signInFailed: (method, code) => track('sign_in_failed', { method, error_code: code || 'unknown' }),
  signUpStart: () => track('sign_up_start'),
  signUpDone: () => track('sign_up_complete'),
  signUpFailed: code => track('sign_up_failed', { error_code: code || 'unknown' }),
  paywallShown: (reason = 'no_subscription') => track('paywall_shown', { reason }),
  checkoutStarted: plan => track('checkout_started', { plan }),
  checkoutFailed: (plan, code) => track('checkout_failed', { plan, error_code: code || 'unknown' }),
  portalOpened: () => track('billing_portal_opened'),

  // --- map usage ---
  appOpenMap: () => track('app_open_map'),

  /**
   * `group` separates the mutually-exclusive base maps from the additive
   * overlay families (overlay / aster / dea / terrain), so a report can ask
   * "which ASTER products get used" without knowing every layer id.
   * `source` distinguishes a deliberate click from a preset being applied.
   */
  layerToggled: (layer, visible, group = 'overlay', source = 'user') =>
    track('layer_toggled', { layer, visible: visible ? 'on' : 'off', layer_group: group, source }),

  baseMapChanged: (id, from) => track('base_map_changed', { base_map: id, from_base_map: from || 'unknown' }),

  /**
   * What a session actually ran with, sampled once the map settles.
   *
   * This is the one that answers "what are users leveraging": toggles only
   * capture people who CHANGE something, and the majority never do — they load
   * the default preset and work. Without this, the default configuration looks
   * unused precisely because it is the default.
   */
  overlayState: ({
    baseMap, overlays, overlayCount, aster, asterCount, dea, deaCount, hillshade, zoom,
    asterOpacity, deaOpacity, geomadYear, geomadStyle,
    imagerySensor, imageryMode, imageryRange, imageryStyle,
  }) =>
    track('overlay_state', {
      base_map: baseMap,
      overlays,
      overlay_count: overlayCount,
      aster_layers: aster,
      aster_count: asterCount,
      dea_layers: dea,
      dea_count: deaCount,
      hillshade: hillshade ? 'on' : 'off',
      zoom_bucket: zoomBucket(zoom),
      // Non-boolean settings: invisible to toggle tracking, but they are just
      // as much "what this session ran with" as the checkboxes are.
      aster_opacity: pct(asterOpacity),
      dea_opacity: pct(deaOpacity),
      geomad_year: geomadYear || 'none',
      geomad_style: geomadStyle || 'none',
      imagery_sensor: imagerySensor || 'none',
      imagery_mode: imageryMode || 'none',
      imagery_range: imageryRange || 'none',
      imagery_style: imageryStyle || 'none',
    }),

  /**
   * A deliberate change to an imagery control. The Sentinel-2 time-series work
   * (single / compare / clearing-scan, S2 vs Landsat, date range) is a whole
   * feature area that toggle tracking cannot see, because none of it is a
   * checkbox — it is which MODE of one layer someone chose.
   */
  imageryOptionChanged: (option, value) =>
    track('imagery_option_changed', { option, value: String(value ?? 'none').slice(0, 100) }),

  /** How hard people blend the additive raster groups. 0 opacity is a layer
   *  that is nominally "on" but contributing nothing — worth telling apart. */
  overlayOpacityChanged: (group, opacity) =>
    track('overlay_opacity_changed', { layer_group: group, opacity_pct: pct(opacity) }),

  /**
   * Seconds a layer was actually on screen, flushed when the session ends.
   * Separates a layer people leave on all day from one they flick on, squint
   * at and turn off — both of which look identical in toggle counts.
   */
  overlayEngagement: (layer, seconds, group) =>
    track('overlay_engagement', {
      layer,
      layer_group: group,
      seconds,
      duration_bucket: seconds < 10 ? '0-10s'
        : seconds < 60 ? '10-60s'
        : seconds < 300 ? '1-5m'
        : seconds < 1800 ? '5-30m'
        : '30m+',
    }),
  mapView: (zoom, baseMap) =>
    track('map_view', { zoom: Math.round(zoom * 10) / 10, zoom_bucket: zoomBucket(zoom), base_map: baseMap }),
  searchRun: hits => track('search_run', { hits }),
  presetApplied: id => track('preset_applied', { preset: id }),
  presetSaved: () => track('preset_saved'),

  /**
   * Which filters people actually narrow on. `params` carries only bucketed or
   * counted values — never a raw holder name or an exact grade, both of which
   * describe what the user is hunting for.
   */
  filtersApplied: (layer, params = {}) => track('filters_applied', { layer, ...params }),

  /** What people click on to read. The overlays that get INSPECTED are the
   *  ones carrying the value, which is not the same as the ones left visible. */
  featureInspected: kind => track('feature_inspected', { kind }),
  drawingSaved: kind => track('drawing_saved', { kind }),
  waypointSaved: () => track('waypoint_saved'),

  // --- outputs ---
  exportGenerated: (format, kind, count) =>
    track('export_generated', { format, kind, feature_count: count ?? 0 }),
  permit40EGenerated: () => track('permit_40e_generated'),
  sampleReported: kind => track('sample_reported', { kind }),

  // --- admin ---
  adminCorrectionApplied: (kind, count) =>
    track('admin_correction_applied', { kind, row_count: count ?? 0 }),
};

/** 0..1 opacity -> nearest 10%, so the dimension stays low-cardinality. */
function pct(v) {
  if (typeof v !== 'number' || Number.isNaN(v)) return -1;
  return Math.round((v <= 1 ? v * 100 : v) / 10) * 10;
}

function zoomBucket(zoom) {
  if (zoom == null) return 'unknown';
  if (zoom < 8) return 'state';
  if (zoom < 10.6) return 'region';
  if (zoom < 13) return 'district';
  if (zoom < 16) return 'prospect';
  return 'detail';
}

export { analytics, perf };
